import { useQuery } from '@tanstack/react-query';
import { Ban, Eye } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import {
  type Approval,
  ApprovalBadge,
  ApprovalChips,
  type ApprovalFilterValue,
  PendingBanner,
  approvalFromUrl,
} from '../approvals/approval.js';

interface GiftCertificate {
  _id: string;
  code: string;
  initial_value_minor: number;
  balance_minor: number;
  status: 'active' | 'depleted' | 'revoked';
  expires_at?: string;
  created_at: string;
  /** 372: absent on certificates issued before #1240 (approved). */
  approval?: Approval;
}

interface GiftCertificatesResponse {
  items: GiftCertificate[];
  total: number;
  page: number;
  /** 372: waiting for approval, whatever the filter. */
  pending_count?: number;
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(minor: number) {
  return `RM ${(minor / 100).toFixed(2)}`;
}

export function GiftCertificateListPage() {
  // 063 (FR-013): a gift certificate is tender; only an administrator may issue one.
  const ability = useAbility();
  const canIssue = ability.can('create', 'GiftCertificate');
  // 372: the list needs only `read` now; revoking still needs `manage` (as the server asks).
  const canRevoke = ability.can('manage', 'GiftCertificate');
  // 162 (#21): this list already had a pager; what it lacked was a way to find one certificate.
  const { page, setPage, searchInput, setSearchInput, search } = useListControls();
  // 372 (#1240): which approval state to list.
  const { search: urlSearch } = useLocation();
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilterValue>(() =>
    approvalFromUrl(urlSearch),
  );

  const listPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: '20' });
    // Only when there is something to filter by (FR-004).
    if (search) params.set('q', search);
    if (approvalFilter) params.set('approval', approvalFilter);
    return `/gift-certificates?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.giftCertificate.list, page, search, approvalFilter],
    queryFn: () => api.get<GiftCertificatesResponse>(listPath),
    // 063 (FR-010): a refusal is not a transient fault. Without this the caller
    // reads the empty state through three retries before the truth arrives.
    retry: false,
  });

  const revoke = useConfirmedMutation({
    mutationFn: (id: string) => api.post(`/gift-certificates/${id}/revoke`),
    invalidateKey: RESOURCE_KEYS.giftCertificate.list,
  });

  const columns: Column<GiftCertificate>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (gc) => (
        <Link to={`/gift-certificates/${gc._id}`} className="text-primary underline">
          {gc.code}
        </Link>
      ),
    },
    {
      key: 'initial_value_minor',
      header: 'Initial Value',
      render: (gc) => formatMoney(gc.initial_value_minor),
    },
    {
      key: 'balance_minor',
      header: 'Balance',
      render: (gc) => formatMoney(gc.balance_minor),
    },
    {
      key: 'status',
      header: 'Status',
      // 372: a certificate waiting for approval, or rejected, says so before anything else. 401 (#1366): unless it
      // is revoked, which nothing outranks.
      render: (gc) =>
        gc.status !== 'revoked' && gc.approval && gc.approval.status !== 'approved' ? (
          <ApprovalBadge approval={gc.approval} />
        ) : (
          <StatusBadge status={gc.status} />
        ),
    },
    {
      key: 'issued_by',
      header: 'Issued by',
      render: (gc) => gc.approval?.requested_by_name || '—',
    },
    { key: 'expires_at', header: 'Expires', render: (gc) => formatDate(gc.expires_at) },
    {
      key: '_id',
      header: 'Actions',
      render: (gc) => (
        <RowActions
          actions={[
            {
              key: 'view',
              label: 'View',
              href: `/gift-certificates/${gc._id}`,
              icon: <Eye className="h-4 w-4" />,
            },
            canRevoke &&
              gc.status !== 'revoked' && {
                key: 'revoke',
                label: 'Revoke',
                variant: 'danger',
                confirmMessage: `Revoke gift certificate "${gc.code}"? It can no longer be redeemed.`,
                onClick: () => revoke.run(gc._id),
                icon: <Ban className="h-4 w-4" />,
              },
          ]}
        />
      ),
    },
  ];

  const pages = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;

  return (
    <div>
      <PageHeader
        title="Gift Certificates"
        description="Issued gift certificates and their remaining balances"
        actions={
          canIssue ? (
            <Link to="/gift-certificates/new">
              <Button>+ Issue Certificate</Button>
            </Link>
          ) : undefined
        }
      />
      <PendingBanner count={data?.pending_count ?? 0} kind="certificate" />
      <ApprovalChips
        value={approvalFilter}
        onChange={(v) => {
          setApprovalFilter(v);
          setPage(1);
        }}
        pendingCount={data?.pending_count}
      />
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search gift certificates by code"
        testId="gift-certificate-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r._id}
        emptyMessage="No gift certificates issued yet"
        // 063: administrator-only. "None issued yet" is a claim about the
        // business, and a 403 is no evidence for it.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: pages,
          onPageChange: setPage,
        }}
      />{' '}
    </div>
  );
}
